<script setup lang="ts">
import { useField } from 'vee-validate';
import { computed, inject, ref, watch } from 'vue';
import type { Ref } from 'vue';
import DOMPurify from 'dompurify';
import type { QEditor } from 'quasar';
import type { Locales } from '@/services/types/api.types';

const props = withDefaults(
  defineProps<{
    name: string;
    label: string;
    language_enabled: Locales[];
    disableSanitizeHtml?: boolean;
  }>(),
  { disableSanitizeHtml: false },
);

const nameFr = computed(() => `${props.name}.fr`);
const nameEn = computed(() => `${props.name}.en`);

const { value: valueFr, errorMessage: errorMessageFr, setValue: setValueFr } = useField<string>(nameFr.value);
const { value: valueEn, errorMessage: errorMessageEn, setValue: setValueEn } = useField<string>(nameEn.value);

const editLocale = inject<Ref<Locales>>('editLocale');

const editorFr = ref<QEditor>();
const editorEn = ref<QEditor>();

watch(
  () => editLocale?.value,
  (newValue) => {
    if (newValue === 'fr' && !valueFr.value) setValueFr('', false);
    if (newValue === 'en' && !valueEn.value) setValueEn('', false);
  },
);

const handlePaste = (event: ClipboardEvent) => {
  const target = event.target;
  if (target instanceof HTMLElement && target.classList.contains('q-editor__link-input')) return;

  if (props.disableSanitizeHtml) return;
  event.preventDefault();
  event.stopPropagation();
  const text = event.clipboardData?.getData('text/html') || event.clipboardData?.getData('text/plain') || '';
  const allowedTags = ['a', 'br', 'div', 'em', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'ol', 'p', 'strong', 'ul'];
  const allowedAttributes = ['href'];
  const sanitized = DOMPurify.sanitize(text, { ALLOWED_TAGS: allowedTags, ALLOWED_ATTR: allowedAttributes });
  if (!editLocale?.value) return;
  const editor = editLocale.value === 'fr' ? editorFr : editorEn;
  editor?.value?.runCmd('insertHTML', sanitized);
};
</script>

<template>
  <div class="localized-editor">
    <div v-if="editLocale === 'fr'">
      <q-editor
        ref="editorFr"
        v-model="valueFr"
        :label="props.label"
        v-bind="$attrs"
        :error="!!errorMessageFr"
        :error-message="errorMessageFr"
        :toolbar="[
          ['left', 'center', 'right', 'justify'],
          ['bold', 'italic', 'strike', 'underline'],
          ['undo', 'redo'],
          ['hr', 'link'],
          ['unordered', 'outdent', 'indent'],
          ['removeFormat'],
        ]"
        @paste="handlePaste"
      />
      <span class="errorMessage" v-if="errorMessageFr">{{ errorMessageFr }}</span>
    </div>
    <div v-else-if="editLocale === 'en'">
      <q-editor
        ref="editorEn"
        v-model="valueEn"
        :label="props.label"
        v-bind="$attrs"
        :error="!!errorMessageEn"
        :error-message="errorMessageEn"
        :toolbar="[
          ['left', 'center', 'right', 'justify'],
          ['bold', 'italic', 'strike', 'underline'],
          ['undo', 'redo'],
          ['hr', 'link'],
          ['unordered', 'outdent', 'indent'],
          ['removeFormat'],
        ]"
        @paste="handlePaste"
      />
      <span class="errorMessage" v-if="errorMessageEn">{{ errorMessageEn }}</span>
    </div>
    <q-banner class="text-white bg-red q-mb-sm" v-if="!editLocale">Missing editLocale injection</q-banner>
  </div>
</template>
<style lang="scss">
.localized-editor {
  .errorMessage {
    font-size: 12px;
    color: $color-red;
    padding-left: 12px;
  }
}
</style>
